/**
 * Sistema Global de Manejo de Errores para Aerotucán
 * Previene errores silenciosos y proporciona logging detallado
 */

class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 100;
    this.enableConsoleLog = true;
    this.enableLocalStorage = true;
  }

  log(error, context = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message || String(error),
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Agregar a la cola de errores
    this.errors.push(errorEntry);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift(); // Remover el más antiguo
    }

    // Log a consola
    if (this.enableConsoleLog) {
      console.error('🚨 [Aerotucán Error]', {
        message: errorEntry.message,
        context,
        timestamp: errorEntry.timestamp
      });
      if (error.stack) {
        console.error('Stack trace:', error.stack);
      }
    }

    // Guardar en localStorage para debugging
    if (this.enableLocalStorage) {
      try {
        const storedErrors = JSON.parse(localStorage.getItem('aerotucan_errors') || '[]');
        storedErrors.push(errorEntry);
        // Mantener solo los últimos 50 errores
        const recentErrors = storedErrors.slice(-50);
        localStorage.setItem('aerotucan_errors', JSON.stringify(recentErrors));
      } catch (e) {
        console.warn('No se pudo guardar error en localStorage:', e);
      }
    }

    // En producción, aquí enviarías a un servicio como Sentry
    // this.sendToMonitoring(errorEntry);
  }

  info(message, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: 'INFO',
      message,
      data
    };

    if (this.enableConsoleLog) {
      console.log('ℹ️ [Aerotucán]', message, data);
    }
  }

  warn(message, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: 'WARN',
      message,
      data
    };

    if (this.enableConsoleLog) {
      console.warn('⚠️ [Aerotucán Warning]', message, data);
    }
  }

  getErrors() {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
    if (this.enableLocalStorage) {
      localStorage.removeItem('aerotucan_errors');
    }
  }

  // Para producción: enviar a servicio de monitoreo
  sendToMonitoring(errorEntry) {
    // Aquí integrarías con Sentry, LogRocket, etc.
    // Ejemplo:
    // Sentry.captureException(new Error(errorEntry.message), {
    //   contexts: { custom: errorEntry.context }
    // });
  }
}

// Singleton instance
const errorLogger = new ErrorLogger();

// Capturar errores globales no manejados
window.addEventListener('error', (event) => {
  errorLogger.log(event.error || new Error(event.message), {
    type: 'UNHANDLED_ERROR',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

// Capturar promesas rechazadas no manejadas
window.addEventListener('unhandledrejection', (event) => {
  errorLogger.log(new Error(event.reason), {
    type: 'UNHANDLED_PROMISE_REJECTION'
  });
});

export default errorLogger;

// Helper para envolver funciones con manejo de errores
export const withErrorHandling = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      errorLogger.log(error, { ...context, functionArgs: args });
      throw error; // Re-lanzar para que el componente pueda manejarlo
    }
  };
};
